import React from "react";
import "./NoteDetails.css";
import { formatDateTimeAsStored } from "../../utils/utils";

const NoteDetails = ({ selectedNote }) => {
  if (!selectedNote) {
    return <div className="nd-empty">No note selected</div>;
  }

  const data = selectedNote.metadata ? selectedNote.metadata : selectedNote;

  const formattedAccessionNo = data?.report?.accessionNumber?.replace(/-/g, "") || "";
  const depositorCIK = data?.report?.depositor?.depositorCIK;
  const primaryDocument = data?.report?.primaryDocument;
  const secLink = depositorCIK && formattedAccessionNo && primaryDocument
    ? `https://www.sec.gov/Archives/edgar/data/${depositorCIK}/${formattedAccessionNo}/${primaryDocument}`
    : null;

  const walletAddress =
    data?.holder?.organization?.walletAddress ??
    data?.holder?.holder?.organization?.walletAddress ??
    data?.walletAddress;
  const wallet = walletAddress ? `${walletAddress.slice(0, 8)}…${walletAddress.slice(-8)}` : "—";

  const txHash = data?.noteTxHash;
  const orgStatus = data?.holder?.holder?.organization?.status ?? data?.holder?.organization?.status;

  return (
    <div className="nd-container fade-in">
      <header className="nd-hero">
        <div className="nd-hero-main">
          <div className="nd-eyebrow">
            <i className="bi bi-journal-text" />
            <span>Note · #{data?.id ?? "—"}</span>
          </div>
          <h1 className="nd-title">{data?.noteName || "Untitled note"}</h1>
          <div className="nd-hero-meta">
            <span className={`status-pill ${data?.burnt ? "status-burnt" : "status-active"}`}>
              {data?.burnt ? "Burnt" : "Active"}
            </span>
            <span className="status-pill">{data?.control || "—"}</span>
            {data?.balance != null && (
              <span className="nd-balance">${Number(data.balance).toLocaleString()}</span>
            )}
          </div>
        </div>

        {txHash && (
          <div className="nd-hero-side">
            <span className="nd-side-label">On-chain transaction</span>
            <a
              className="tx-hash"
              href={`${process.env.REACT_APP_EXP_URL}${txHash}`}
              target="_blank"
              rel="noopener noreferrer"
              title={txHash}
              style={{ maxWidth: 280 }}
            >
              <i className="bi bi-box-arrow-up-right" style={{ marginRight: 6 }} />
              {txHash.slice(0, 10)}…{txHash.slice(-8)}
            </a>
          </div>
        )}
      </header>

      <div className="nd-grid">
        <Section title="Note" icon="bi-journal-text">
          <Field k="Note name"           v={data?.noteName} />
          <Field k="Note ID"             v={data?.id} />
          <Field k="Loan ID"             v={data?.loanId} />
          <Field k="Loan name"           v={data?.loan?.loanName} />
          <Field k="Control"             v={data?.control} />
          <Field k="Balance"             v={data?.balance != null ? `$${Number(data.balance).toLocaleString()}` : null} />
          <Field k="Balance description" v={data?.balanceDescription} wide />
          <Field k="Created"             v={formatDateTimeAsStored(data?.createdAt)} />
          <Field k="Updated"             v={formatDateTimeAsStored(data?.updatedAt)} />
        </Section>

        <Section title="Report" icon="bi-file-earmark-text">
          <Field k="Report ID"         v={data?.report?.id} />
          <Field k="Deal"              v={data?.report?.deal?.deal} />
          <Field k="Deal ID"           v={data?.report?.deal?.id} />
          <Field k="Depositor"         v={data?.report?.depositor?.depositor} />
          <Field k="Depositor ID"      v={data?.report?.depositor?.id} />
          <Field k="Accession number"  v={data?.report?.accessionNumber} mono />
          <Field
            k="Primary document"
            v={
              data?.report?.primaryDocument ? (
                secLink ? (
                  <a className="tx-hash" href={secLink} target="_blank" rel="noopener noreferrer" title={data.report.primaryDocument}>
                    {data.report.primaryDocument}
                  </a>
                ) : (
                  <span>{data.report.primaryDocument}</span>
                )
              ) : null
            }
            wide
          />
          <Field k="Filing date"   v={formatDateTimeAsStored(data?.report?.filingDate)} />
          <Field k="Document date" v={formatDateTimeAsStored(data?.report?.documentDate)} />
          <Field k="Closing date"  v={formatDateTimeAsStored(data?.report?.closingDate)} />
        </Section>

        <Section title="Holder & organization" icon="bi-buildings">
          <Field k="Holder"        v={data?.holder?.holderName} />
          <Field k="Holder ID"     v={data?.holder?.id} />
          <Field k="Holder CIK"    v={data?.holder?.holderCIK} mono />
          <Field k="Parent holder" v={data?.holder?.holder?.holderName ?? data?.holder?.holderName} />
          <Field k="Organization"  v={data?.holder?.holder?.organization?.name ?? data?.holder?.organization?.name} />
          <Field k="Org type"      v={data?.holder?.holder?.organization?.type ?? data?.holder?.organization?.type} />
          <Field k="Org status"    v={orgStatus
            ? <span className={`status-pill status-${String(orgStatus).toLowerCase()}`}>{orgStatus}</span>
            : null} />
          <Field k="Address"       v={data?.holder?.holder?.organization?.address ?? data?.holder?.organization?.address} wide />
          <Field k="Email"         v={data?.holder?.holder?.organization?.email ?? data?.holder?.organization?.email} />
          <Field k="Wallet"        v={<span className="mono" title={walletAddress || ""}>{wallet}</span>} />
        </Section>
      </div>
    </div>
  );
};

const Section = ({ title, icon, children }) => (
  <section className="nd-section card">
    <header className="nd-section-head">
      <i className={`bi ${icon}`} />
      <h3>{title}</h3>
    </header>
    <dl className="nd-fields">{children}</dl>
  </section>
);

const Field = ({ k, v, mono, wide }) => {
  const empty = v == null || v === "" || v === undefined;
  return (
    <div className={`nd-field ${wide ? "nd-field-wide" : ""}`}>
      <dt>{k}</dt>
      <dd className={mono ? "mono" : ""}>{empty ? <span className="muted">—</span> : v}</dd>
    </div>
  );
};

export default NoteDetails;
